import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { sendEmail } from "../../lib/send-email";
import { createPortal } from "react-dom";

// Modal-specific styles to prevent external CSS interference
const modalStyles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 99999,
        padding: '16px',
        boxSizing: 'border-box',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    modalContainer: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        maxWidth: '500px',
        width: '100%',
        padding: '24px',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
        color: '#333',
        border: '1px solid rgba(229, 231, 235, 1)',
        boxSizing: 'border-box'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(229, 231, 235, 1)',
        paddingBottom: '16px',
        marginBottom: '16px'
    },
    title: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#1f2937',
        margin: 0
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    description: {
        marginBottom: '24px',
        fontSize: '14px',
        color: '#6b7280'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    label: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151',
        display: 'block',
        marginBottom: '4px'
    },
    input: {
        width: '100%',
        padding: '12px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#1f2937',
        backgroundColor: '#fff',
        boxSizing: 'border-box'
    },
    textarea: {
        width: '100%',
        padding: '12px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#1f2937',
        backgroundColor: '#fff',
        minHeight: '120px',
        resize: 'vertical',
        boxSizing: 'border-box'
    },
    buttonGroup: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '16px'
    },
    cancelButton: {
        padding: '10px 20px',
        backgroundColor: '#fff',
        color: '#374151',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer',
        fontWeight: '500'
    },
    submitButton: {
        padding: '10px 20px',
        backgroundColor: '#1E88E5',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer',
        fontWeight: '500'
    },
    disabledButton: {
        opacity: '0.5',
        cursor: 'not-allowed'
    },
    error: {
        color: '#ef4444',
        fontSize: '14px',
        marginTop: '4px'
    },
    successMessage: {
        backgroundColor: '#10b981',
        color: '#fff',
        padding: '12px',
        borderRadius: '8px',
        marginTop: '16px',
        border: '1px solid #059669',
        fontSize: '14px'
    },
    errorMessage: {
        backgroundColor: '#ef4444',
        color: '#fff',
        padding: '12px',
        borderRadius: '8px',
        marginTop: '16px',
        border: '1px solid #dc2626',
        fontSize: '14px'
    }
};

export default function ContactModal({ isOpen, onClose, contactEmail, data }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [isSuccess, setIsSuccess] = useState(false); // Track success state
    
    const onContactSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {   
            if (!name || !email || !message) {
                setError("Please fill in all fields");
                setIsSubmitting(false);
                return;
            }

            if (!contactEmail) {
                setError("No email address provided");
                setIsSubmitting(false);
                return;
            }

            const result = await sendEmail(
                "tech@witnium.com",
                contactEmail,
                `Contact Request from ${name}`,
                `
                <h2>New Contact Request</h2>
                <p><strong>Name:</strong> ${name}</p>
                <p><strong>Email:</strong> ${email}</p>
                <p><strong>Message:</strong> ${message}</p>
                <p><strong>Sent at:</strong> ${new Date().toLocaleString()}</p>
                <hr>
                <p>Please respond to this inquiry as soon as possible.</p>
                `
            );
            if (result) {
                setIsSuccess(true);
                setError("");
                setIsSubmitting(false);
                setName("");
                setEmail("");
                setMessage("");
            } else {
                setError("Failed to send message");
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error("Failed to send contact email:", error);
            alert("We couldn't process your contact request. Please try again later.");
            throw error;
        }
    }

    if (!isOpen) return null; // Only render if isOpen is true

    // Use createPortal to render the modal at the root level of the document
    return createPortal(
        <div style={modalStyles.overlay}>
            <div style={modalStyles.modalContainer}>
                {/* Header */}
                <div style={modalStyles.header}>
                    <h3 style={modalStyles.title}>📩 {data.title}</h3>
                    <button onClick={onClose} style={modalStyles.closeButton}>
                        <X size={24} />
                    </button>
                </div>

                {/* Description */}
                <div style={modalStyles.description}>
                    <p>{data.description}</p>
                </div>

                {/* Form */}
                <form onSubmit={onContactSubmit} style={modalStyles.form}>
                    <div style={modalStyles.formGroup}>
                        <label htmlFor="name" style={modalStyles.label}>
                            {data.input_name_label} <span style={{color: '#ef4444'}}>*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={data.input_name_placeholder}
                            style={modalStyles.input}
                        />
                    </div>

                    <div style={modalStyles.formGroup}>
                        <label htmlFor="email" style={modalStyles.label}>
                            {data.input_email_label} <span style={{color: '#ef4444'}}>*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={data.input_email_placeholder}
                            style={modalStyles.input}
                        />
                    </div>

                    <div style={modalStyles.formGroup}>
                        <label htmlFor="message" style={modalStyles.label}>
                            {data.input_message_label} <span style={{color: '#ef4444'}}>*</span>
                        </label>
                        <textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={data.input_message_placeholder}
                            style={modalStyles.textarea}
                        />
                    </div>

                    {error && <div style={modalStyles.error}>{data.input_message_error}</div>}

                    {/* Buttons */}
                    <div style={modalStyles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={modalStyles.cancelButton}
                        >
                            {data.cancel_button_text}
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                ...modalStyles.submitButton,
                                ...(isSubmitting ? modalStyles.disabledButton : {})
                            }}
                        >
                            {isSubmitting ? 'Submitting...' : data.send_button_text}
                        </button>
                    </div>
                    
                    {error && <div style={modalStyles.errorMessage}>{data.error_message}</div>}
                    {isSuccess && <div style={modalStyles.successMessage}>{data.success_message}</div>}
                </form>
            </div>
        </div>,
        document.body
    );
}
